import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../../../../auth'
import { showErrorToast } from '../../../../../shared/utils/apiErrorMessage'
import {
  getCrewList,
  getSignOnOffRecords,
  getRanks,
  getVesselList,
  getCrewDashboardSummary,
  getCrewTrainings,
  type CrewDashboardSummary,
} from '../../../core/_requests'
import type { Crew } from '../../../core/_models'
import type { CrewDashboardData, CrewDerivedKpis } from '../types'

export function useCrewDashboard() {
  const { currentUser } = useAuth()
  const roleId = Number((currentUser as any)?.role?.id ?? 0)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [crewList, setCrewList] = useState<Crew[]>([])
  const [signOnOffRecords, setSignOnOffRecords] = useState<any[]>([])
  const [ranks, setRanks] = useState<any[]>([])
  const [vessels, setVessels] = useState<any[]>([])
  const [trainings, setTrainings] = useState<any[]>([])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const results = await Promise.allSettled([
        getCrewList(),
        getSignOnOffRecords(),
        getRanks(),
        getVesselList(),
        getCrewTrainings(),
      ])

      const [crewRes, signRes, rankRes, vesselRes, trainingRes] = results

      if (crewRes.status === 'fulfilled') setCrewList(crewRes.value || [])
      if (signRes.status === 'fulfilled') setSignOnOffRecords(signRes.value || [])
      if (rankRes.status === 'fulfilled') setRanks(rankRes.value || [])
      if (vesselRes.status === 'fulfilled') setVessels(vesselRes.value || [])
      if (trainingRes.status === 'fulfilled') setTrainings(trainingRes.value || [])

      const rejected = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined
      if (rejected) {
        const msg = showErrorToast(rejected.reason, 'Failed to fetch some crew dashboard metrics.')
        setError(msg)
      }
    } catch (err) {
      const msg = showErrorToast(err, 'Failed to load crew dashboard data.')
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const summary = useMemo<CrewDashboardSummary>(
    () => getCrewDashboardSummary(crewList, signOnOffRecords, ranks),
    [crewList, signOnOffRecords, ranks]
  )

  const data: CrewDashboardData = useMemo(() => ({
    crewList,
    signOnOffRecords,
    ranks,
    vessels,
    trainings,
    summary,
  }), [crewList, signOnOffRecords, ranks, vessels, trainings, summary])

  const kpis: CrewDerivedKpis = useMemo(() => ({
    totalCrew: summary.totalCrew,
    onboardNow: summary.onboardNow,
    pendingApprovals: summary.pendingApprovals,
    expiringCerts: summary.certificationsExpiringSoon,
    signedOff: signOnOffRecords.filter((r: any) => r.status === 'SIGNED_OFF').length,
    vessels: vessels.length,
  }), [summary, signOnOffRecords, vessels])

  return {
    loading,
    error,
    data,
    kpis,
    summary,
    crewList,
    signOnOffRecords,
    ranks,
    vessels,
    trainings,
    refresh: fetchAll,
  }
}
